import { useQuery } from '@tanstack/react-query';
import { isFirebaseConfigured } from '@/firebase';
import { callDetectPlatform } from '@/services/functionsService';
import { detectPlatformFromUrl } from '@/utils/url';
import { useDebouncedValue } from './useDebouncedValue';
import type { MediaMetadata, Platform } from '@/types';

export interface PlatformPreview {
  platform: Platform | null;
  metadata: MediaMetadata | null;
  publiclyAccessible: boolean | null;
  isLoading: boolean;
}

/** Detect the platform locally, then fetch public metadata for a live preview. */
export function usePlatformPreview(url: string): PlatformPreview {
  const platform = detectPlatformFromUrl(url);
  const debouncedUrl = useDebouncedValue(url, 600);
  const enabled =
    isFirebaseConfigured && platform !== null && detectPlatformFromUrl(debouncedUrl) === platform;

  const query = useQuery({
    queryKey: ['detect', debouncedUrl],
    enabled,
    queryFn: () => callDetectPlatform({ url: debouncedUrl }),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  return {
    platform,
    metadata: query.data?.metadata ?? null,
    publiclyAccessible: query.data ? query.data.publiclyAccessible : null,
    isLoading: enabled && query.isPending,
  };
}
